import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Button, EmptyState, vars } from "@pack-it-up/ui-web";
import type { TripSummary } from "@pack-it-up/core";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { useStores } from "../../app/AppContext.js";
import { useSession } from "../../app/SessionProvider.js";
import { fmtTripDates } from "../../lib/format.js";
import { useDayTick } from "../../lib/useDayTick.js";
import { ArchiveRow } from "../shared/ArchiveRow.js";
import { ConfirmDeleteDialog } from "../shared/ConfirmDeleteDialog.js";
import { PageHeader } from "../shared/PageHeader.js";
import { SearchBox, matchesName, useSearch } from "../shared/SearchBox.js";
import { SectionHead } from "../shared/SectionHead.js";
import { TripGrid } from "../shared/TripGrid.js";
import { NewTripModal } from "../home/NewTripModal.js";

// My Trips (issue #163): upcoming, past, and archived trips in one place — Home only
// shows upcoming trips, and there's no longer a combined trips+templates archive page.
export const MyTripsPage = (): ReactNode => {
  const { home, archive, pendingTripDelete } = useSession();
  const { auth } = useStores();
  const navigate = useNavigate();
  const isAnonymous = useStoreValue(auth, (s) => s.user?.isAnonymous ?? false);

  const trips = useStoreValue(home, (s) => s.trips);
  const rawArchivedTrips = useStoreValue(archive, (s) => s.trips);
  const dayTick = useDayTick();
  const groups = useMemo(() => home.getState().groups(), [home, trips, dayTick]);
  // The delete write is deferred behind an Undo toast (AppShell), so the underlying
  // lists still have it — hide it here until the write actually lands (`hiddenIds`
  // outlives the toast itself, so nothing flickers back into view while the write is
  // in flight). A trip can be deleted from any of the three sections below.
  const hiddenTripIds = useStoreValue(pendingTripDelete, (s) => s.hiddenIds);
  const activeTrips = useMemo(
    () => [...groups.upcoming, ...groups.dateless].filter((t) => !hiddenTripIds.includes(t.id)),
    [groups, hiddenTripIds],
  );
  const pastTrips = useMemo(() => groups.past.filter((t) => !hiddenTripIds.includes(t.id)), [groups, hiddenTripIds]);
  const archivedTrips = useMemo(
    () => rawArchivedTrips.filter((t) => !hiddenTripIds.includes(t.id)),
    [rawArchivedTrips, hiddenTripIds],
  );

  // Search filters every section; the section counts stay whole-list, as on Home.
  const { query, filtered, onChange: handleSearch } = useSearch();
  const search = (list: TripSummary[]): TripSummary[] =>
    filtered === "" ? list : list.filter((t) => matchesName(t.name, filtered));
  const shownActive = search(activeTrips);
  const shownPast = search(pastTrips);
  const shownArchived = search(archivedTrips);

  const [showNewTrip, setShowNewTrip] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<TripSummary | null>(null);

  const openTrip = (id: string): void => {
    navigate(`/trip/${id}`);
  };

  const confirmDelete = async (): Promise<void> => {
    if (pendingDelete === null) return;
    const t = pendingDelete;
    setPendingDelete(null);
    await archive.getState().deleteTrip(t.id);
  };

  return (
    <>
      <PageHeader
        title="My Trips"
        right={<SearchBox value={query} onChange={handleSearch} placeholder="Search trips…" />}
      />

      <div style={{ padding: `${vars.space.xl} ${vars.space.xl} calc(${vars.space.xl} * 2)` }}>
        <section>
          <SectionHead
            title="Upcoming Trips"
            count={`${activeTrips.length} ${activeTrips.length === 1 ? "trip" : "trips"}`}
          />
          {activeTrips.length === 0 ? (
            <EmptyState
              title="No upcoming trips"
              description="Plan your next trip to start packing."
              {...(isAnonymous ? {} : { action: <Button onClick={() => setShowNewTrip(true)}>New trip</Button> })}
            />
          ) : shownActive.length === 0 ? (
            <p style={{ color: vars.color.textMuted, marginBottom: vars.space.md }}>No trips match your search.</p>
          ) : (
            <TripGrid trips={shownActive} onOpen={openTrip} {...(isAnonymous ? {} : { onNew: () => setShowNewTrip(true) })} />
          )}
        </section>

        {pastTrips.length > 0 && (
          <section>
            <SectionHead
              title="Past Trips"
              count={`${pastTrips.length} ${pastTrips.length === 1 ? "trip" : "trips"}`}
            />
            {shownPast.length === 0 ? (
              <p style={{ color: vars.color.textMuted, marginBottom: vars.space.md }}>No trips match your search.</p>
            ) : (
              <TripGrid trips={shownPast} onOpen={openTrip} />
            )}
          </section>
        )}

        <section>
          <SectionHead
            title="Archived Trips"
            count={`${archivedTrips.length} ${archivedTrips.length === 1 ? "trip" : "trips"}`}
          />
          {archivedTrips.length === 0 ? (
            <p style={{ color: vars.color.textMuted }}>No archived trips.</p>
          ) : shownArchived.length === 0 ? (
            <p style={{ color: vars.color.textMuted }}>No trips match your search.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: vars.space.sm }}>
              {shownArchived.map((t) => (
                <ArchiveRow
                  key={t.id}
                  name={t.name}
                  meta={fmtTripDates(t)}
                  onOpen={() => openTrip(t.id)}
                  onRestore={() => void archive.getState().unarchiveTrip(t.id)}
                  onDelete={() => setPendingDelete(t)}
                />
              ))}
            </div>
          )}
        </section>
      </div>

      <NewTripModal open={showNewTrip} onClose={() => setShowNewTrip(false)} />

      <ConfirmDeleteDialog
        name={pendingDelete?.name ?? null}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => void confirmDelete()}
      />
    </>
  );
};
